import type { ControlMode, Participant, RoomSettings } from '@kinop/shared';
import { LIMITS } from '@kinop/shared';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from './UserAvatar.js';

interface ParticipantListProps {
  participants: Participant[];
  myClientId: string | null;
  controlMode: ControlMode;
  settings: RoomSettings;
  amIHost: boolean;
  onChangeControlMode: (mode: ControlMode) => void;
  onChangeSettings: (patch: Partial<RoomSettings>) => void;
}

/** Готовые значения следа рисунка. Своё вводится числом рядом. */
const TRAIL_PRESETS = [1, 4, 8, 30, 120];

export function ParticipantList({
  participants,
  myClientId,
  controlMode,
  settings,
  amIHost,
  onChangeControlMode,
  onChangeSettings,
}: ParticipantListProps): JSX.Element {
  const rows: {
    key: keyof RoomSettings | 'anyonePause';
    title: string;
    sub: string;
    value: boolean;
    toggle: () => void;
  }[] = [
    {key:'reactions',title:'Реакции поверх кадра',sub:'Эмодзи видят все участники',value:settings.reactions!==false,toggle:()=>onChangeSettings({reactions:settings.reactions===false})},
    {
      key: 'anyonePause',
      title: 'Паузу может ставить каждый',
      sub: 'иначе начнётся тирания',
      value: controlMode === 'everyone',
      toggle: () => onChangeControlMode(controlMode === 'everyone' ? 'host' : 'everyone'),
    },
    {
      key: 'guestSeek',
      title: 'Гости могут перематывать',
      sub: 'рискованно, но весело',
      value: settings.guestSeek,
      toggle: () => onChangeSettings({ guestSeek: !settings.guestSeek }),
    },
    {
      key: 'drawing',
      title: 'Рисование поверх кадра',
      sub: 'линии растворяются сами',
      value: settings.drawing,
      toggle: () => onChangeSettings({ drawing: !settings.drawing }),
    },
    {
      key: 'autoCatchUp',
      title: 'Автодогон отставших',
      sub: 'подтягиваем без вопросов',
      value: settings.autoCatchUp,
      toggle: () => onChangeSettings({ autoCatchUp: !settings.autoCatchUp }),
    },
    {
      key: 'openRoom',
      title: 'Вход новых участников',
      sub: 'Выключите, чтобы запереть комнату',
      value: settings.openRoom,
      toggle: () => onChangeSettings({ openRoom: !settings.openRoom }),
    },
  ];

  return (
    <section className="participants" aria-label="Участники">
      {amIHost&&<><p className="helper">Доступ</p><div className="choice-row">{([['link','По ссылке'],['public','Для всех'],['approval','По одобрению']] as const).map(([key,label])=><button key={key} aria-pressed={(settings.access??'link')===key} onClick={()=>onChangeSettings({access:key})}>{label}</button>)}</div></>}
      <header className="participants__header">
        <span className="mono-label">Смотрят ({participants.length})</span>
      </header>

      <ul className="participants__list">
        {participants.map((participant) => (
          <li key={participant.clientId} className="participants__item">
            <UserAvatar nickname={participant.nickname} avatar={participant.avatar} />

            <span className="participants__name">
              <span className="participants__nickname">{participant.nickname}</span>
              {participant.clientId === myClientId && <Badge variant="secondary">вы</Badge>}
              {participant.isHost && <Badge>ведущий</Badge>}
            </span>

            {/* Пояснение текстом, а не в title: подсказку из title не
                покажут ни клавиатуре, ни пальцу на телефоне. */}
            {participant.isBuffering && (
              <Badge variant="warning" className="shrink-0">
                догружает
              </Badge>
            )}
          </li>
        ))}
      </ul>

      {amIHost && (
        <div className="room-settings">
          <span className="mono-label">Настройки комнаты</span>

          <div className="room-settings__rows">
            {rows.map((row) => (
              <button
                key={row.key}
                type="button"
                className="toggle-row"
                role="switch"
                aria-checked={row.value}
                onClick={row.toggle}
              >
                <span className="toggle-row__text">
                  <span className="toggle-row__title">{row.title}</span>
                  <span className="toggle-row__sub">{row.sub}</span>
                </span>
                <span className="toggle" data-on={row.value || undefined}>
                  <span className="toggle__knob" />
                </span>
              </button>
            ))}
          </div>

          <div className="trail">
            <span className="trail__title">След рисунка</span>
            <span className="trail__sub">через сколько линии растворяются</span>
            <div className="trail__row">
              {TRAIL_PRESETS.map((seconds) => (
                <button
                  key={seconds}
                  type="button"
                  className="pill"
                  data-on={settings.trailSeconds === seconds ? '' : undefined}
                  aria-pressed={settings.trailSeconds === seconds}
                  onClick={() => onChangeSettings({ trailSeconds: seconds })}
                >
                  {seconds} с
                </button>
              ))}

              {/* Своё значение: пресеты покрывают привычные случаи, но кто-то
                  хочет, чтобы рисунок висел минуту. */}
              <label
                className="pill pill--input"
                data-on={TRAIL_PRESETS.includes(settings.trailSeconds) ? undefined : ''}
              >
                <input
                  type="number"
                  className="mono"
                  min={LIMITS.minTrailSeconds}
                  max={LIMITS.maxTrailSeconds}
                  value={settings.trailSeconds}
                  aria-label="Свой след рисунка в секундах"
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    if (Number.isFinite(value)) onChangeSettings({ trailSeconds: value });
                  }}
                />
                <span>своё, с</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
